import type { ProductCost, Customer } from '../../api/common/model/customer';
import type { DiscountsType } from '../../components/discount/Discounts';

const getDiscountPercentage = (
  identifier: 'ANNUAL_DEDUCTION' | 'AGENCY',
  subscription?: Customer['subscription'],
): number | undefined =>
  subscription?.products
    .flatMap((product) => product.componentsPercentageDeduction)
    .find(
      (discount) =>
        discount.applicationScope === 'SUBSCRIPTION_SCOPE' && discount.identifier === identifier,
    )?.percentage;

export const getAgencyDiscount = (
  purchasedProductCosts?: ProductCost[],
  withPrice = false,
  subscription?: Customer['subscription'],
): DiscountsType | undefined => {
  const agencyProduct = purchasedProductCosts?.find((product) => {
    return product.componentCosts.some((component) => component.componentIdentifier === 'AGENCY');
  });

  return agencyProduct === undefined
    ? undefined
    : {
        type: 'agency',
        state: 'applied',
        amount: withPrice ? agencyProduct?.amountWithoutTax : undefined,
        percentage: getDiscountPercentage('AGENCY', subscription) ?? 10,
      };
};

export const getYearlyDiscount = (
  purchasedProductCosts?: ProductCost[],
  withPrice = false,
  subscription?: Customer['subscription'],
): DiscountsType | undefined => {
  const yearlyDiscount = purchasedProductCosts?.find((product) => {
    return product.componentCosts.some(
      (component) => component.componentIdentifier === 'ANNUAL_DEDUCTION',
    );
  });

  return yearlyDiscount === undefined
    ? undefined
    : {
        type: 'yearly',
        state: 'applied',
        amount: withPrice ? yearlyDiscount?.amountWithoutTax : undefined,
        percentage: getDiscountPercentage('ANNUAL_DEDUCTION', subscription) ?? 20,
      };
};

export const getAccountDiscounts = (
  purchasedProductCosts?: ProductCost[],
  /**
   * For now we never return the price, as there is still an issue with
   * displaying it correctly with the current BE fields we receive.
   * Also see: https://hotjar.slack.com/archives/C015WCG0DNE/p1674144255805739.
   */
  withPrice = false,
  subscription?: Customer['subscription'],
): DiscountsType[] => {
  const agencyDiscount = getAgencyDiscount(purchasedProductCosts, withPrice, subscription);
  const yearlyDiscount = getYearlyDiscount(purchasedProductCosts, withPrice, subscription);
  return [agencyDiscount, yearlyDiscount].filter(
    (discount): discount is DiscountsType => discount !== undefined,
  );
};
